<template>
  <div
    :class="`question-steps-container max-w-screen-md ${$attrs.class || 'mb-14'} ${
      data.itemProps?.info ? 'pad-question-lable' : ''
    } relative cursor-pointer`"
  >
    <FormKitSchema :data="data" :schema="[schema]" :library="library" />
    <InfoIcon
      v-if="props.showInfoText && data.itemProps.info"
      class="absolute z-5 right-1 top-1 step-item-info-icon"
      @click="toggleShowInfoText"
    />
    <Info
      class="active-question-tooltip block w-full lg:top-0 lg:-right-[calc(100%+72px)] lg:absolute"
      v-if="props.showInfoText && data.itemProps.info"
      v-show="shouldShowInfoTest()"
      :title="`Tipp - ${data.itemProps.label}`"
      :text="<string>data.itemProps?.info"
    />
  </div>
</template>

<script setup lang="ts">
import QuestionTitle from '@/questionnaire/components/QuestionTitle.vue'
import { useQuestionnaireStore } from '@/questionnaire/stores/questionnaire'
import type { QuestionItem, TemplateVariable } from '@/questionnaire/types'
import type { SelectOption } from '@/types'
import countries from '@/utils/countries'
import exhaustiveGuard from '@/utils/exhaustiveGuard'
import type { FormKitNode, FormKitSchemaFormKit } from '@formkit/core'
import { createInput, FormKitSchema } from '@formkit/vue'
import { reactive } from 'vue'
import InfoIcon from '@/assets/icons/16/info.svg?component'
import DaysPerWeekInput from '../Inputs/DaysPerWeekInput.vue'
import type { InfoItem } from '@/admin/types'
import Info from '@/components/InfoDialogue.vue'

const props = withDefaults(
  defineProps<{
    item: QuestionItem
    setInfoText?: ({ label: _label, info: _info }: InfoItem, _tooltipIdentifier?: string) => void
    first?: boolean
    showInfoText?: boolean
    activeInputName?: string
    itemNumber?: number
  }>(),
  {
    setInfoText: undefined,
    first: false,
    showInfoText: true,
    activeInputName: undefined,
    itemNumber: undefined,
  }
)

const getTooltipItemId = () => `${data.itemProps?.name}-stepItem-${props.itemNumber}`

const isElementInputFocused = (elm: Event) => {
  const target = <HTMLElement>elm.target
  const tagName = target?.tagName?.toLowerCase()

  if (['input', 'select', 'radio', 'check'].includes(tagName)) {
    if (props.setInfoText) {
      props.setInfoText(<InfoItem>data.itemProps, `stepItem-${props.itemNumber}`)
    }
  }
}
const shouldShowInfoTest = () => {
  return props.activeInputName === getTooltipItemId()
}

const toggleShowInfoText = () => {
  if (props.setInfoText) {
    if (props.activeInputName === getTooltipItemId()) {
      props.setInfoText(<InfoItem>{ label: '', info: '' })
      return
    }
    props.setInfoText(<InfoItem>data.itemProps, `stepItem-${props.itemNumber}`)
  }
}

const questionnaireStore = useQuestionnaireStore()
const { questions, templateVariables, questionConfigurations } = questionnaireStore

// TODO: move this fallback logic to a place where it's actually easy to understand why this is necessary (do you still know why? ;-))
let templateVariable: TemplateVariable
let question = questions[props.item.question_code]
if (question) {
  templateVariable = templateVariables[question.template_variable_code]
} else {
  templateVariable = templateVariables[props.item.question_code]

  const foundQuestion =
    Object.values(questions).find(({ template_variable_code }) => props.item.question_code === template_variable_code) || {}

  question = {
    ...foundQuestion,
    code: props.item.question_code,
    template_variable_code: props.item.question_code,
    title: templateVariable.label,
  }
}
const questionConfiguration = questionConfigurations[question.code]

const dependenciesResolved = (dependingNode: FormKitNode) => {
  if (!dependingNode) {
    return false
  }
  // another safety guard
  if (!questionConfiguration) {
    return true
  }
  const depends_on = questionConfiguration.depends_on
  if (!depends_on) {
    return true
  }
  const answer = dependingNode.value
  if (answer === undefined || answer === null) {
    return false
  }

  switch (depends_on.validator) {
    case 'filled':
      return answer !== undefined
    case 'oneofstring':
      return depends_on.values.includes(answer as string)
    case 'eq':
      return answer === depends_on.value
    case 'neq':
      return answer !== depends_on.value
    default:
      exhaustiveGuard(depends_on)
  }

  return false
}
const value = questionnaireStore.getTemplateVariableValue(templateVariable.code)
const input_type = templateVariable.input_type
const itemProps: Record<string, unknown> = {
  name: templateVariable.code,
  itemNumber: props.itemNumber,
  id: templateVariable.code,
  info: question.info,
  validation: 'required',
  validationLabel: templateVariable.label,
  label: question.title,
  placeholder: question.placeholder,
  disabled: question.display_only,
  onClick: props.showInfoText ? isElementInputFocused : () => {},
  value,
  sectionsSchema: {
    outer: {
      attrs: { 'data-first': props.first },
    },
  },
}

const daysPerWeek = createInput(DaysPerWeekInput, { props: ['min', 'max'] })

const mapOptions = (select_options: SelectOption[]) => {
  return select_options.map(so => {
    return { label: so.label, value: so.code }
  })
}
switch (input_type) {
  case 'boolean':
    itemProps.type = 'radio'
    itemProps.options = [
      { label: 'Ja', value: true },
      { label: 'Nein', value: false },
    ]
    itemProps.value = <string>itemProps.value === 'true'
    break
  case 'singleselect':
    itemProps.type = 'radio'
    itemProps.options = mapOptions(templateVariable.select_options)
    break
  case 'multiselect':
    itemProps.type = 'checkbox'
    itemProps.options = mapOptions(templateVariable.select_options)
    break
  case 'countryselect':
    itemProps.type = 'autocomplete'
    itemProps.options = countries.map(({ alpha2, name }) => ({ label: name, value: alpha2 }))
    break
  case 'currency':
    itemProps.type = 'currency'
    // itemProps.suffixIcon = 'euro'
    itemProps.displayLocale = 'DE-de'
    itemProps.min = 0
    itemProps.currency = 'EUR'
    break
  case 'daysperweek':
    itemProps.type = daysPerWeek
  // falls through to number
  case 'number':
    itemProps.type ||= 'number'
    if (templateVariable.min) {
      itemProps.min = templateVariable.min
      itemProps.validation += `|min:${templateVariable.min}`
    }
    if (templateVariable.max) {
      itemProps.max = templateVariable.max
      itemProps.validation += `|max:${templateVariable.max}`
    }
    break
  case 'daterange':
  case 'date':
    itemProps.type = 'datepicker'
    itemProps.format = { date: 'long' }
    itemProps.pickerOnly = true
    itemProps.overlay = true
    itemProps.valueFormat = 'YYYY-MM-DD'
    break
  case 'text':
    itemProps.type = 'text'
    break
  default:
    exhaustiveGuard(input_type)
}

const data = reactive({
  itemProps,
  dependenciesResolved,
})

const library = { QuestionTitle }
const schema: FormKitSchemaFormKit = {
  $formkit: itemProps.type as string,
  bind: '$itemProps',
}

if (questionConfiguration?.depends_on) {
  schema.if = `$dependenciesResolved($get(${questionConfiguration.depends_on.template_variable_code}))`
}
</script>

<style>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.question-steps-container > .step-item-info-icon,
.question-steps-container > .active-question-tooltip {
  display: none;
}

.question-steps-container > .formkit-outer ~ .step-item-info-icon,
.question-steps-container > .formkit-outer ~ .active-question-tooltip {
  display: block;
}

.pad-question-lable label.formkit-label {
  padding-right: 1.5rem;
}
</style>
